import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { Checkbox } from './ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiLoading, mdiPlus } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { usePropertyMappingRules, useCreatePropertyMappingRule } from '../hooks/usePropertyMappingRulesApi'
import { useAssetTypes } from '../api/queries'
import { apiClient } from '../api/client'
import { useLocale } from '../contexts/LocaleContext'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { AssetTypeTile, type AssetTypeLike } from './assets/AssetTypeTile'
import { EmptyState } from './ui/empty-state'

interface DocumentType {
  uid: string
  code: string
  name: Record<string, string>
}

function getLocalizedName(name: string | Record<string, string> | undefined, locale: string): string {
  if (!name) return 'Unnamed'
  if (typeof name === 'string') return name
  return name[locale] || name.en || name.fr || Object.values(name)[0] || 'Unnamed'
}

export function PropertyMappingRulesPage() {
  const { org } = useParams({ strict: false })
  const { locale } = useLocale()
  const navigate = useNavigate()
  const { t } = useTranslation('properties')
  const [searchQuery, setSearchQuery] = useState('')
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('all')
  const [assetTypeFilter, setAssetTypeFilter] = useState<string>('all')
  const [enabledFilter, setEnabledFilter] = useState<string>('all')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newRule, setNewRule] = useState({
    code: '',
    document_type_uid: '',
    asset_type_uid: '',
    enabled: true,
  })

  useBreadcrumbs(useMemo(() => [
    { title: t('mapping_rules.breadcrumb_home'), path: `/${org}` },
    { title: t('mapping_rules.breadcrumb_settings'), path: `/${org}/settings` },
    { title: t('mapping_rules.breadcrumb_mappings') }
  ], [org, t]))

  // Fetch document types
  const { data: documentTypes = [] } = useQuery({
    queryKey: ['documentTypes', org],
    queryFn: async (): Promise<DocumentType[]> => {
      const response = await apiClient.GET('/api/v1/organizations/{orgCode}/document-types', {
        params: { path: { orgCode: org! }, query: { limit: 1000 } }
      })
      return (response.data as any)?.data || []
    },
    enabled: !!org,
  })

  const { data: assetTypes = [] } = useAssetTypes(org || 'demo')

  const params: Record<string, string> = {}
  if (documentTypeFilter !== 'all') params.document_type_uid = documentTypeFilter
  if (assetTypeFilter !== 'all') params.asset_type_uid = assetTypeFilter
  if (enabledFilter !== 'all') params.enabled = enabledFilter

  const { data, isLoading, error } = usePropertyMappingRules(org || 'demo', params)
  const rules = data?.rules || []
  const total = data?.total || 0

  const filteredRules = useMemo(() => {
    if (!searchQuery.trim()) return rules
    const q = searchQuery.toLowerCase()
    return rules.filter((r: any) =>
      (r.code || '').toLowerCase().includes(q) ||
      getLocalizedName(r.document_type?.name, locale).toLowerCase().includes(q) ||
      getLocalizedName(r.asset_type?.name, locale).toLowerCase().includes(q)
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, searchQuery])

  const createMutation = useCreatePropertyMappingRule(org || 'demo')

  const handleCreate = () => {
    createMutation.mutate({
      code: newRule.code || undefined,
      document_type_uid: newRule.document_type_uid,
      asset_type_uid: newRule.asset_type_uid,
      enabled: newRule.enabled,
    }, {
      onSuccess: (rule) => {
        setIsCreateDialogOpen(false)
        setNewRule({ code: '', document_type_uid: '', asset_type_uid: '', enabled: true })
        navigate({
          to: '/$org/property-mapping-rules/$uid',
          params: { org: org || 'demo', uid: rule.uid },
        })
      },
    })
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.mappingRules}
            title={t('mapping_rules.title')}
            description={t('mapping_rules.subtitle', { total })}
            actions={
              <>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <Can perm="configuration.write">
              <DialogTrigger asChild>
                <Button data-testid="create-rule-btn">
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  {t('mapping_rules.create_button')}
                </Button>
              </DialogTrigger>
            </Can>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('mapping_rules.create_dialog_title')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 p-4">
                <div>
                  <Label htmlFor="code">{t('mapping_rules.code_label')}</Label>
                  <Input
                    id="code"
                    placeholder={t('mapping_rules.code_placeholder')}
                    value={newRule.code}
                    onChange={(e) => setNewRule(prev => ({ ...prev, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                    data-testid="code-input"
                  />
                </div>
                <div>
                  <Label>{t('mapping_rules.document_type_label')}</Label>
                  <Select value={newRule.document_type_uid} onValueChange={(v) => setNewRule(prev => ({ ...prev, document_type_uid: v }))}>
                    <SelectTrigger data-testid="document-type-select">
                      <SelectValue placeholder={t('mapping_rules.document_type_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map((dt) => (
                        <SelectItem key={dt.uid} value={dt.uid}>{getLocalizedName(dt.name, locale)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('mapping_rules.asset_type_label')}</Label>
                  <Select value={newRule.asset_type_uid} onValueChange={(v) => setNewRule(prev => ({ ...prev, asset_type_uid: v }))}>
                    <SelectTrigger data-testid="asset-type-select">
                      <SelectValue placeholder={t('mapping_rules.asset_type_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map((at) => (
                        <SelectItem key={at.uid} value={at.uid}>
                          <span className="flex items-center gap-2">
                            <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                            <span>{getLocalizedName(at.name as any, locale)}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={newRule.enabled}
                    onCheckedChange={(checked) => setNewRule(prev => ({ ...prev, enabled: checked === true }))}
                    data-testid="enabled-checkbox"
                  />
                  <Label>{t('mapping_rules.enabled_label')}</Label>
                </div>
                {createMutation.isError && (
                  <div className="text-sm text-destructive">
                    {(createMutation.error as any)?.detail || (createMutation.error as any)?.title || t('mapping_rules.create_failed')}
                  </div>
                )}
                <Button
                  onClick={handleCreate}
                  disabled={createMutation.isPending || !newRule.document_type_uid || !newRule.asset_type_uid}
                  data-testid="save-entry-btn"
                  className="w-full"
                >
                  {createMutation.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" /> : null}
                  {t('mapping_rules.create_rule_button')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
              </>
            }
          />
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-4 flex-wrap">
            <Input
              placeholder={t('mapping_rules.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
              data-testid="rules-search"
            />
            <Select value={documentTypeFilter} onValueChange={setDocumentTypeFilter}>
              <SelectTrigger className="w-[200px]" data-testid="document-type-filter">
                <SelectValue placeholder={t('mapping_rules.filter_all_document_types')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('mapping_rules.filter_all_document_types')}</SelectItem>
                {documentTypes.map((dt) => (
                  <SelectItem key={dt.uid} value={dt.uid}>{getLocalizedName(dt.name, locale)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={assetTypeFilter} onValueChange={setAssetTypeFilter}>
              <SelectTrigger className="w-[200px]" data-testid="asset-type-filter">
                <SelectValue placeholder={t('mapping_rules.filter_all_asset_types')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('mapping_rules.filter_all_asset_types')}</SelectItem>
                {assetTypes.map((at) => (
                  <SelectItem key={at.uid} value={at.uid}>
                    <span className="flex items-center gap-2">
                      <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                      <span>{getLocalizedName(at.name as any, locale)}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={enabledFilter} onValueChange={setEnabledFilter}>
              <SelectTrigger className="w-[150px]" data-testid="enabled-filter">
                <SelectValue placeholder={t('mapping_rules.filter_all_status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('mapping_rules.filter_all_status')}</SelectItem>
                <SelectItem value="true">{t('mapping_rules.filter_enabled')}</SelectItem>
                <SelectItem value="false">{t('mapping_rules.filter_disabled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-8">{t('mapping_rules.error')}</div>
          ) : filteredRules.length === 0 ? (
            <EmptyState variant="inline" live title={t('mapping_rules.empty')} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('mapping_rules.column_code')}</TableHead>
                  <TableHead>{t('mapping_rules.column_document_type')}</TableHead>
                  <TableHead>{t('mapping_rules.column_asset_type')}</TableHead>
                  <TableHead>{t('mapping_rules.column_enabled')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRules.map((rule: any) => (
                  <TableRow
                    key={rule.uid}
                    className="cursor-pointer"
                    data-testid={`rule-row-${rule.uid}`}
                  >
                    <TableCell>
                      <Link
                        to="/$org/property-mapping-rules/$uid"
                        params={{ org: org!, uid: rule.uid }}
                        className="text-primary hover:underline font-medium"
                      >
                        {rule.code ? (
                          <code className="text-xs bg-muted px-2 py-1 rounded">{rule.code}</code>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/$org/property-mapping-rules/$uid"
                        params={{ org: org!, uid: rule.uid }}
                        className="text-primary hover:underline"
                      >
                        {getLocalizedName(rule.document_type?.name, locale)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <AssetTypeTile type={rule.asset_type as AssetTypeLike} size="sm" />
                        <span>{getLocalizedName(rule.asset_type?.name, locale)}</span>
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={rule.enabled ? 'default' : 'outline'}>
                        {rule.enabled ? t('mapping_rules.status_enabled') : t('mapping_rules.status_disabled')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  )
}
